'use client';

import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import AddToCartButton from '@/components/cart/AddToCartButton';

import type { Product } from '@/lib/products';
import { useI18n } from '@/i18n/LocaleProvider';
import { formatDate, formatPrice } from '@/i18n/config';

interface ProductCardProps {
  product: Product;
  className?: string;
}

export default function ProductCard({ product, className = '' }: ProductCardProps) {
  const { locale, t } = useI18n();
  const typeColors: Record<string, string> = {
    'Booster Box': 'text-primary border-primary/30 bg-primary/10',
    'Booster Pack': 'text-accent border-accent/30 bg-accent/10',
    'Elite Trainer Box': 'text-purple-300 border-purple-500/30 bg-purple-500/10',
    'Bundle': 'text-teal-300 border-teal-500/30 bg-teal-500/10',
    'Collector Box': 'text-pink-300 border-pink-500/30 bg-pink-500/10',
  };

  return (
    <Link
      href={`/product?slug=${product.slug}`}
      className={`group block glass-card rounded-xl overflow-hidden product-card-hover border-top-glow ${className}`}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden bg-muted/20">
        {/* Soft glow behind the product */}
        <div className="absolute inset-0 product-image-glow pointer-events-none" />
        <AppImage
          src={product.image}
          alt={product.imageAlt}
          fill
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          className="object-contain transition-transform duration-700 p-4 product-image-shadow group-hover:scale-105"
        />

        {/* Type badge */}
        <div className="absolute top-3 right-3">
          <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${typeColors[product.type] || 'text-muted-foreground border-border'}`}>
            {product.type}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <p className="text-xs text-muted-foreground font-medium mb-1 uppercase tracking-wider">{product.set}</p>
        <h3 className="text-sm font-bold text-foreground leading-snug mb-3 group-hover:text-primary transition-colors line-clamp-2">
          {product.name}
        </h3>
        {product.preorder && (
          <p className="-mt-2 mb-3 text-xs font-semibold text-accent">{t.shop.releases(formatDate(product.preorder.releaseDate, locale))}</p>
        )}
        <div className="flex items-center justify-between">
          <span className="text-lg font-bold accent-text-gradient">{formatPrice(product.price, locale)}</span>
          <AddToCartButton product={product} />
        </div>
      </div>
    </Link>
  );
}